<!-- RegisterEmployeeModal.vue -->
<template>
  <BaseModal
    :is-visible="isVisible"
    title="Register New Employee"
    subtitle="Create a new employee account"
    :icon="UserPlus"
    size="2xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <div class="space-y-6">
      <form @submit.prevent="handleSubmit" @keydown.enter="handleEnterKeyPress" class="space-y-6">
        <!-- Email -->
        <div class="form-group">
          <label
            for="email"
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            Email Address <span class="text-red-500">*</span>
          </label>
          <input
            id="email"
            v-model="formData.email"
            type="email"
            class="modern-input w-full"
            :class="{
              'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                errors.email || isEmailInvalid
            }"
            placeholder="Enter employee email address"
            required
          />
          <p v-if="errors.email" class="mt-2 text-sm text-red-600 font-medium">
            {{ errors.email }}
          </p>
        </div>

        <!-- Password Section -->
        <div class="form-group">
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Password <span class="text-red-500">*</span>
          </label>

          <div class="relative">
            <input
              id="password"
              v-model="formData.password"
              :type="showPassword ? 'text' : 'password'"
              class="block w-full px-4 py-3 pr-20 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              :class="{
                'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.password
              }"
              placeholder="Enter password (minimum 8 characters)"
              required
              minlength="8"
            />
            <button
              type="button"
              class="absolute inset-y-0 right-10 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              @click="showPassword = !showPassword"
              :title="showPassword ? 'Hide password' : 'Show password'"
            >
              <svg
                v-if="!showPassword"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
              </svg>
            </button>
            <button
              type="button"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              @click="generatePassword"
              title="Generate secure password"
            >
              <RefreshCw class="h-5 w-5" />
            </button>
          </div>

          <p v-if="errors.password" class="mt-2 text-sm text-red-600 font-medium">
            {{ errors.password }}
          </p>

          <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Password must be at least 8 characters long
          </p>
        </div>

        <!-- Name Fields -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- First Name -->
          <div class="form-group">
            <label
              for="first_name"
              class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              First Name <span class="text-red-500">*</span>
            </label>
            <input
              id="first_name"
              v-model="formData.first_name"
              type="text"
              class="modern-input w-full"
              :class="{
                'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.first_name
              }"
              placeholder="First name"
              required
              maxlength="100"
            />
            <p v-if="errors.first_name" class="mt-2 text-sm text-red-600 font-medium">
              {{ errors.first_name }}
            </p>
          </div>

          <!-- Middle Name -->
          <div class="form-group">
            <label
              for="middle_name"
              class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              Middle Name
            </label>
            <input
              id="middle_name"
              v-model="formData.middle_name"
              type="text"
              class="modern-input w-full"
              :class="{
                'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.middle_name
              }"
              placeholder="Middle name (optional)"
              maxlength="100"
            />
            <p v-if="errors.middle_name" class="mt-2 text-sm text-red-600 font-medium">
              {{ errors.middle_name }}
            </p>
          </div>

          <!-- Last Name -->
          <div class="form-group">
            <label
              for="last_name"
              class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              Last Name <span class="text-red-500">*</span>
            </label>
            <input
              id="last_name"
              v-model="formData.last_name"
              type="text"
              class="modern-input w-full"
              :class="{
                'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.last_name
              }"
              placeholder="Last name"
              required
              maxlength="100"
            />
            <p v-if="errors.last_name" class="mt-2 text-sm text-red-600 font-medium">
              {{ errors.last_name }}
            </p>
          </div>
        </div>

        <!-- Contact Information -->
        <!-- <div class="grid grid-cols-1 md:grid-cols-2 gap-4"> -->
        <div class="grid">
          <!-- Phone Number -->
          <div class="form-group">
            <label
              for="phone_number"
              class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              Phone Number
            </label>
            <input
              id="phone_number"
              v-model="formData.phone_number"
              type="tel"
              class="modern-input w-full"
              :class="{
                'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.phone_number
              }"
              placeholder="(555) 123-4567"
              maxlength="20"
            />
            <p v-if="errors.phone_number" class="mt-2 text-sm text-red-600 font-medium">
              {{ errors.phone_number }}
            </p>
          </div>

          <!-- User Type (Optional, Uncomment if Needed, also the div container) -->
          <!-- <div class="form-group">
                      <label
                        for="user_type"
                        class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                      >
                        User Type
                      </label>
                      <select
                        id="user_type"
                        v-model="formData.user_type"
                        class="modern-input w-full"
                        :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.user_type }"
                      >
                        <option value="1">employee</option>
                        <option value="0">Admin</option>
                      </select>
                      <p v-if="errors.user_type" class="mt-2 text-sm text-red-600 font-medium">
                        {{ errors.user_type }}
                      </p>
                    </div> -->
        </div>

        <!-- Address Information -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- City -->
          <div class="form-group">
            <label
              for="city"
              class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              City
            </label>
            <input
              id="city"
              v-model="formData.city"
              type="text"
              class="modern-input w-full"
              :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.city }"
              placeholder="City"
              maxlength="100"
            />
            <p v-if="errors.city" class="mt-2 text-sm text-red-600 font-medium">
              {{ errors.city }}
            </p>
          </div>

          <!-- State -->
          <div class="form-group">
            <label
              for="state"
              class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              State
            </label>
            <input
              id="state"
              v-model="formData.state"
              type="text"
              class="modern-input w-full"
              :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.state }"
              placeholder="State"
              maxlength="100"
            />
            <p v-if="errors.state" class="mt-2 text-sm text-red-600 font-medium">
              {{ errors.state }}
            </p>
          </div>

          <!-- Zip Code -->
          <div class="form-group">
            <label
              for="zip_code"
              class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              Zip Code
            </label>
            <input
              id="zip_code"
              v-model="formData.zip_code"
              type="text"
              class="modern-input w-full"
              :class="{
                'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.zip_code
              }"
              placeholder="12345"
              maxlength="20"
            />
            <p v-if="errors.zip_code" class="mt-2 text-sm text-red-600 font-medium">
              {{ errors.zip_code }}
            </p>
          </div>
        </div>

        <!-- Status (Optional) -->
        <!-- <div class="form-group">
                    <label
                      for="user_status"
                      class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Account Status
                    </label>
                    <select
                      id="user_status"
                      v-model="formData.user_status"
                      class="modern-input w-full"
                      :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.user_status }"
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                    <p v-if="errors.user_status" class="mt-2 text-sm text-red-600 font-medium">
                      {{ errors.user_status }}
                    </p>
                  </div> -->

        <!-- Live validation messages -->
        <div
          class="mt-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
        >
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Form Requirements
          </h4>
          <div class="space-y-2">
            <!-- Password validation -->
            <div class="flex items-center text-sm">
              <CheckCircle
                v-if="isPasswordValidLength && isPasswordHasSymbol"
                class="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
              />
              <AlertTriangle
                v-else-if="formData.password && formData.password.length > 0"
                class="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0"
              />
              <div
                v-else
                class="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 mr-2 flex-shrink-0"
              ></div>
              <span
                :class="{
                  'text-green-600 dark:text-green-400 font-medium': isPasswordValidLength && isPasswordHasSymbol,
                  'text-yellow-600 dark:text-yellow-400':
                    formData.password && formData.password.length > 0 && (!isPasswordValidLength || !isPasswordHasSymbol),
                  'text-gray-600 dark:text-gray-400':
                    !formData.password || formData.password.length === 0
                }"
              >
                Password (min 8 chars, 1 symbol)
              </span>
            </div>

            <!-- Email validation -->
            <div class="flex items-center text-sm">
              <div class="flex-shrink-0 mr-3">
                <CheckCircle
                  v-if="!isEmailEmpty && !isEmailInvalid"
                  class="w-4 h-4 text-green-500"
                />
                <div
                  v-else-if="isEmailEmpty"
                  class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"
                ></div>
                <AlertTriangle v-else class="w-4 h-4 text-amber-500" />
              </div>
              <span
                :class="{
                  'text-green-600 dark:text-green-400 font-medium':
                    !isEmailEmpty && !isEmailInvalid,
                  'text-amber-600 dark:text-amber-400': isEmailInvalid,
                  'text-gray-600 dark:text-gray-400': isEmailEmpty
                }"
              >
                {{
                  isEmailEmpty
                    ? 'Enter a valid email address'
                    : isEmailInvalid
                      ? 'Please check your email format'
                      : 'Valid email address ✓'
                }}
              </span>
            </div>

            <!-- Password validation -->
            <div class="flex items-center text-sm">
              <div class="flex-shrink-0 mr-3">
                <CheckCircle
                  v-if="formData.password && isPasswordValidLength && isPasswordHasSymbol"
                  class="w-4 h-4 text-green-500"
                />
                <AlertTriangle
                  v-else-if="formData.password && (!isPasswordValidLength || !isPasswordHasSymbol)"
                  class="w-4 h-4 text-amber-500"
                />
                <div
                  v-else
                  class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"
                ></div>
              </div>
              <span
                :class="{
                  'text-green-600 dark:text-green-400 font-medium':
                    formData.password && isPasswordValidLength && isPasswordHasSymbol,
                  'text-amber-600 dark:text-amber-400': formData.password && (!isPasswordValidLength || !isPasswordHasSymbol),
                  'text-gray-600 dark:text-gray-400': !formData.password
                }"
              >
                {{
                  !formData.password
                    ? 'Enter a password (min 8 chars, 1 symbol)'
                    : (!isPasswordValidLength && !isPasswordHasSymbol)
                      ? `Needs ${Math.max(0, 8 - formData.password.length)} more chars & 1 symbol`
                      : !isPasswordValidLength
                        ? `Needs ${Math.max(0, 8 - formData.password.length)} more chars`
                        : !isPasswordHasSymbol
                          ? 'Needs at least 1 symbol'
                          : 'Password meets requirements ✓'
                }}
              </span>
            </div>

            <!-- Name validation -->
            <div class="flex items-center text-sm">
              <div class="flex-shrink-0 mr-3">
                <CheckCircle
                  v-if="!isFirstNameEmpty && !isLastNameEmpty"
                  class="w-4 h-4 text-green-500"
                />
                <AlertTriangle
                  v-else-if="formData.first_name || formData.last_name"
                  class="w-4 h-4 text-amber-500"
                />
                <div
                  v-else
                  class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"
                ></div>
              </div>
              <span
                :class="{
                  'text-green-600 dark:text-green-400 font-medium':
                    !isFirstNameEmpty && !isLastNameEmpty,
                  'text-amber-600 dark:text-amber-400':
                    (formData.first_name || formData.last_name) &&
                    (isFirstNameEmpty || isLastNameEmpty),
                  'text-gray-600 dark:text-gray-400': !formData.first_name && !formData.last_name
                }"
              >
                {{
                  isFirstNameEmpty && isLastNameEmpty
                    ? 'Enter first and last name'
                    : isFirstNameEmpty
                      ? 'First name is still needed'
                      : isLastNameEmpty
                        ? 'Last name is still needed'
                        : 'Name information complete ✓'
                }}
              </span>
            </div>

            <!-- Overall status -->
            <div v-if="isFormValid" class="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
              <div
                class="flex items-center text-sm text-green-600 dark:text-green-400 font-semibold"
              >
                <CheckCircle class="w-4 h-4 mr-2" />
                Ready to register employee
              </div>
            </div>
          </div>
        </div>


      </form>
    </div>

    <!-- Footer buttons -->
    <template #footer>
      <BaseButton text="Cancel" variant="secondary" :disabled="isLoading" @click="closeModal" />
      <BaseButton
        :text="isLoading ? 'Registering...' : 'Register'"
        variant="primary"
        :disabled="isLoading || !isFormValid"
        :loading="isLoading"
        @click="handleSubmit"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import AuthService from '@/services/auth'
import { useToastService } from '@/services/toast'
import {
  UserPlus,
  AlertTriangle,
  CheckCircle,
  RefreshCw
} from 'lucide-vue-next'

interface Props {
  isVisible: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'success'): void
}

interface RegistrationData {
  email: string
  password: string
  password_confirmation: string
  user_type: number
  user_status: number
  first_name: string
  middle_name: string
  last_name: string
  city: string
  state: string
  zip_code: string
  phone_number: string
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const toast = useToastService()

// Form state
const formData = ref<RegistrationData>({
  email: '',
  password: '',
  password_confirmation: '',
  user_type: 1, // Default to employee
  user_status: 1, // Default to Active
  first_name: '',
  middle_name: '',
  last_name: '',
  city: '',
  state: '',
  zip_code: '',
  phone_number: '',
})

const errors = ref<Record<string, string>>({})
const isLoading = ref(false)

// Password visibility toggles
const showPassword = ref(false)

// Computed
const isFormValid = computed(() => {
  return (
    formData.value.email.length > 0 &&
    !isEmailEmpty.value &&
    !isEmailInvalid.value &&
    formData.value.password.length > 0 && // Password must be generated
    formData.value.password.length >= 8 && // Password must meet minimum length
    isPasswordHasSymbol.value && // Password must have at least one symbol
    formData.value.first_name.length > 0 &&
    formData.value.last_name.length > 0
  )
})

// Live validation computed properties
const isPasswordValidLength = computed(() => {
  return formData.value.password && formData.value.password.length >= 8
})

const isPasswordHasSymbol = computed(() => {
  return formData.value.password && /[!@#$%^&*(),.?":{}|<>_~\-+=]/.test(formData.value.password)
})

// Email validation computed property
const isEmailInvalid = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return (
    formData.value.email &&
    formData.value.email.trim() !== '' &&
    !emailRegex.test(formData.value.email)
  )
})

const isEmailEmpty = computed(() => {
  return !formData.value.email || formData.value.email.trim() === ''
})

// Name validation computed properties
const isFirstNameEmpty = computed(() => {
  return !formData.value.first_name || formData.value.first_name.trim() === ''
})

const isLastNameEmpty = computed(() => {
  return !formData.value.last_name || formData.value.last_name.trim() === ''
})

// Methods
const generatePassword = () => {
  // Randomize length between 10 and 12 (inclusive)
  const length = Math.floor(Math.random() * 3) + 10 // 0..2 -> 10..12
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
  let password = ''

  // Ensure at least one lowercase, one uppercase, one number, and one special character
  password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]
  password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]
  password += '0123456789'[Math.floor(Math.random() * 10)]
  password += '!@#$%^&*'[Math.floor(Math.random() * 8)]

  // Fill the rest with random characters
  for (let i = 4; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)]
  }

  // Shuffle the password to randomize the position of required characters
  password = password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('')

  formData.value.password = password
  formData.value.password_confirmation = password // Auto-confirm password
}

const resetForm = () => {
  formData.value = {
    email: '',
    password: '',
    password_confirmation: '',
    user_type: 1,
    user_status: 1,
    first_name: '',
    middle_name: '',
    last_name: '',
    city: '',
    state: '',
    zip_code: '',
    phone_number: ''
  }
  errors.value = {}
  showPassword.value = false
}

const closeModal = () => {
  if (!isLoading.value) {
    resetForm()
    emit('close')
  }
}

const validateForm = (): boolean => {
  errors.value = {}

  // Required fields validation
  if (!formData.value.email) {
    errors.value.email = 'Email is required'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    errors.value.email = 'Please enter a valid email address'
  }

  if (!formData.value.password) {
    errors.value.password = 'Password is required'
  } else if (formData.value.password.length < 8) {
    errors.value.password = 'Password must be at least 8 characters long'
  } else if (!/[!@#$%^&*(),.?":{}|<>_~\-+=]/.test(formData.value.password)) {
    errors.value.password = 'Password must contain at least one symbol'
  }

  if (!formData.value.first_name) {
    errors.value.first_name = 'First name is required'
  }

  if (!formData.value.last_name) {
    errors.value.last_name = 'Last name is required'
  }

  // Auto-set password confirmation to match password
  formData.value.password_confirmation = formData.value.password

  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isLoading.value = true
  errors.value = {}

  try {
    const payload = {
      email: formData.value.email,
      password: formData.value.password,
      password_confirmation: formData.value.password_confirmation,
      user_type: formData.value.user_type,
      user_status: formData.value.user_status,
      first_name: formData.value.first_name,
      middle_name: formData.value.middle_name || undefined,
      last_name: formData.value.last_name,
      city: formData.value.city || undefined,
      state: formData.value.state || undefined,
      zip_code: formData.value.zip_code || undefined,
      phone_number: formData.value.phone_number || undefined,
    }

    const response = await AuthService.registerEmployee(payload)

    if (response.success) {
      isLoading.value = false
      closeModal()

      // Show different success messages based on whether it's a reactivation or new registration
      if (response.data.is_reactivation) {
        toast.success('Employee account reactivated successfully with updated details')
      } else {
        toast.success('Employee registered successfully')
      }

      emit('success')
    } else {
      toast.error('Failed to register employee', response.message)
    }
  } catch (err: any) {
    console.error('Employee registration failed:', err)

    // Handle validation errors
    if (err?.errors) {
      // Map backend validation errors to form errors
      Object.keys(err.errors).forEach(field => {
        errors.value[field] = err.errors[field][0]
      })

      // Show specific toast message for email already registered
      if (err.errors.email && err.errors.email[0].includes('already')) {
        toast.error(
          'This email address is already registered. Please use a different email or check if the employee already exists.'
        )
      } else {
        // Show generic validation error toast for other validation errors
        toast.error('Please check the form fields and try again.')
      }
    } else {
      const message = err?.message || 'Failed to register employee'
      toast.error('Error', message)
    }
  } finally {
    isLoading.value = false
  }
}

const handleEnterKeyPress = (event: KeyboardEvent) => {
  // Prevent default form submission behavior
  event.preventDefault()

  // Only submit if the form is valid and not currently loading
  if (isFormValid.value && !isLoading.value) {
    handleSubmit()
  }
}

// Watch for modal visibility changes to reset form
watch(
  () => props.isVisible,
  newValue => {
    if (!newValue) {
      resetForm()
    }
  }
)

// Watch for password changes to auto-update confirmation
watch(
  () => formData.value.password,
  newPassword => {
    formData.value.password_confirmation = newPassword
  }
)
</script>

<style scoped>
/* Modern input styling */
.modern-input {
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease-in-out;
  background-color: white;
  color: #111827;
}

.modern-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modern-input::placeholder {
  color: #6b7280;
}

.modern-input:hover {
  border-color: #9ca3af;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Dark mode - using class-based approach to match Tailwind */
.dark .modern-input {
  border-color: #4b5563;
  background-color: #374151;
  color: white;
}

.dark .modern-input::placeholder {
  color: #9ca3af;
}

.dark .modern-input:hover {
  border-color: #6b7280;
}

.dark .modern-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style>
